import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "ag-grid-community/styles/ag-grid.css"; // Mandatory CSS required by the grid
import "ag-grid-community/styles/ag-theme-quartz.css"; // Optional Theme applied to the grid
import { AgGridReact } from "ag-grid-react"; // AG Grid Component
import { VariantsAPI, WeighmentSitesAPI } from "api";
import CardDropdown from "components/common/CardDropdown";
import IconButton from "components/common/IconButton";
import TableHeaderDropdown from "components/common/TableHeaderDropdown";
import { settings } from "config";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Button, Card, Dropdown, Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { MdOutlineModeEdit } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { CONSTANT_ROUTES } from "routes";
import PageTitle from "utils/PageTitle";
import {
  resetColumnState,
  restoreColumnState,
  saveColumnState,
} from "utils/tablesUtils";
import "./styles.css";
import BreadCrumbTable from "components/breadcrumb/BreadCrumbTable";
import { MarqueeMessagesAPI } from "../../../api";


const MarqueeMessageTableTestOne = forwardRef(
  ({ onSelect, refresh, onAddNew, searchText }, ref) => {
    let isDarkBool = localStorage.getItem("isDark") === true;
    let isDarkTheme = localStorage.getItem("theme") === "dark";
    let auto = localStorage.getItem("theme") === "auto";

    const columnApiRef = useRef();
    const gridRef = useRef();

    const [colDefs] = useState([
      {
        field: "message",
        headerName: "Message",
        flex: 2,
        cellStyle: {
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          overflow: "hidden",
        },
      },
      {
        field: "url",
        headerName: "URL",
        flex: 2,
        cellStyle: {
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          overflow: "hidden",
        },
      },
      {
        field: "order",
        headerName: "Display Order",
        flex: 1.75,
        filter: false,
        headerClass: "center-header",
        cellStyle: { display: "flex", justifyContent: "center" },
      },
      {
        field: "status",
        headerName: "Active",
        flex: 1.5,
        filter: false,
        headerClass: "center-header",
        cellRenderer: (p) => (
          <Form.Check
            type="checkbox"
            checked={p.data.status}
            style={{ padding: "10px 0" }}
          />
        ),
        cellStyle: { display: "flex", justifyContent: "center" },
      },
      {
        headerName: "Action",
        field: "action",
        flex: 1.5,
        filter: false,
        headerClass: "center-header",
        cellStyle: { display: "flex", justifyContent: "center" },
        cellRenderer: (p) => (
          <CardDropdown>
            <div className="py-2">
              <Dropdown.Item
                className="d-flex align-items-center gap-2 py-0"
                onClick={() => onSelect(p.data)}
              >
                <MdOutlineModeEdit />
                Edit
              </Dropdown.Item>
            </div>
          </CardDropdown>
        ),
      },
    ]);

    const defaultColDef = {
      sortable: true,
      filter: true,
    };

    // ------------------------ FETCH FUNCTION ------------------------
    const loadRows = useCallback(
      async (api) => {
        try {
          const res = await MarqueeMessagesAPI.get();
          api.setRowData(res?.data?.data || []);
        } catch (e) {
          console.log("Fetch error:", e);
        }
      },
      []
    );

    // ------------------------ GRID READY ------------------------
    const onGridReady = useCallback(
      (params) => {
        gridRef.current = params.api;
        loadRows(params.api);
        restoreState()
      },
      [loadRows]
    );


    const saveState = useCallback(() => {
      saveColumnState("marquess-message-table", columnApiRef);
    }, []);

    const restoreState = useCallback(() => {
      restoreColumnState("marquess-message-table", columnApiRef);
    }, []);

    const resetState = useCallback(() => {
      resetColumnState("marquess-message-table", columnApiRef);
    }, []);

    const onFilterTextBoxChanged = useCallback(() => {
      columnApiRef.current.api.setGridOption(
        "quickFilterText",
        document.getElementById("filter-text-box").value
      );
    }, []);


    useImperativeHandle(ref, () => ({
      saveState: () => saveColumnState("marquess-message-table", columnApiRef),
      restoreState: () => restoreColumnState("marquess-message-table", columnApiRef),
      resetState: () => resetColumnState("marquess-message-table", columnApiRef),
    }));

    // ------------------------ REFETCH WHEN PARENT ASKS ------------------------
    useEffect(() => {
      if (gridRef.current) {
        loadRows(gridRef.current);
      }
    }, [refresh, loadRows]);

    useEffect(() => {
      if (gridRef.current) {
        gridRef.current.setQuickFilter(searchText);
      }
    }, [searchText]);

    return (
      <div className={`${isDarkBool || isDarkTheme || auto
        ? "ag-theme-quartz-dark"
        : "ag-theme-quartz"}`}>
        <Card style={{ height: `calc(100vh - 8rem)` }}>


          <Card.Body className="p-0">
            <AgGridReact
              ref={columnApiRef}
              onGridReady={onGridReady}
              rowSelection="single"
              columnDefs={colDefs}
              defaultColDef={defaultColDef}
              pagination
              paginationPageSize={10}
              onRowDoubleClicked={(params) => onSelect(params.data)}
              overlayNoRowsTemplate="Nothing found - Looks empty over here"
            />
          </Card.Body>
        </Card>
      </div>
    );
  })

export default MarqueeMessageTableTestOne;
